import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  SALES_INVENTORY_BORROWERS_TTL,
  SalesInventoryBorrowersStateModel,
  teamBorrowerDrillDownKey,
} from 'ng2-app/models/sales-inventory/sales-inventory-state.model';
import { BorrowerDrillDownPage } from 'ng2-app/models/sales-inventory/sales-inventory.model';
import { SalesInventoryService } from 'ng2-app/sales-inventory/sales-inventory.service';
import {
  LoadMoreTeamBorrowerDrillDown,
  LoadTeamBorrowerDrillDown,
} from 'ng2-app/store/sales-inventory/team-borrowers.actions';

/**
 * One team's combined borrower drill-down (By-Team mode), keyed by teamBorrowerDrillDownKey.
 * Same shape and keyset pagination as SalesInventoryBorrowersState. Every write is scoped to
 * its own key, so a slow response for a superseded query only fills that query's cache entry.
 * Consumers derive loading from key presence, not from the global `loading`/`fetching` flags.
 */
@State<SalesInventoryBorrowersStateModel>({
  name: 'salesInventoryTeamBorrowers',
  defaults: {
    borrowers: {},
    page: {},
    loadingMore: {},
    loading: false,
    fetching: false,
    lastSync: {},
  },
})
@Injectable()
export class TeamBorrowersState {
  /** Full keyed map (key = teamBorrowerDrillDownKey). */
  @Selector()
  static borrowers(state: SalesInventoryBorrowersStateModel) {
    return state?.borrowers ?? {};
  }

  /** Per-key pagination { nextCursor, hasMore }. */
  @Selector()
  static pages(state: SalesInventoryBorrowersStateModel) {
    return state?.page ?? {};
  }

  /** Per-key "a load-more (append) is in flight" flags. */
  @Selector()
  static loadingMore(state: SalesInventoryBorrowersStateModel) {
    return state?.loadingMore ?? {};
  }

  constructor(private _salesInventoryService: SalesInventoryService) {}

  private isCacheValid(
    state: SalesInventoryBorrowersStateModel,
    key: string,
    force: boolean
  ): boolean {
    if (force) {
      return false;
    }
    const cache = state.lastSync[key];
    if (!cache || !(cache instanceof Date)) {
      return false;
    }
    return cache.getTime() + SALES_INVENTORY_BORROWERS_TTL > Date.now();
  }

  @Action(LoadTeamBorrowerDrillDown)
  loadTeamBorrowerDrillDown(
    ctx: StateContext<SalesInventoryBorrowersStateModel>,
    { teamId, query, overrideCache = false }: LoadTeamBorrowerDrillDown
  ): Observable<BorrowerDrillDownPage> {
    const key = teamBorrowerDrillDownKey(teamId, query);
    const state = ctx.getState();

    if (this.isCacheValid(state, key, overrideCache)) {
      return of({
        rows: state.borrowers[key],
        nextCursor: state.page[key]?.nextCursor ?? null,
        hasMore: state.page[key]?.hasMore ?? false,
      });
    }

    const hasData = !!state.borrowers[key];
    ctx.patchState({ fetching: true, loading: !hasData });

    // First page: replaces any prior rows/cursor for this scope.
    return this._salesInventoryService.getTeamBorrowers(teamId, query).pipe(
      tap((page) => {
        const current = ctx.getState();
        ctx.patchState({
          borrowers: { ...current.borrowers, [key]: page.rows },
          page: { ...current.page, [key]: { nextCursor: page.nextCursor, hasMore: page.hasMore } },
          lastSync: { ...current.lastSync, [key]: new Date() },
          loading: false,
          fetching: false,
        });
      }),
      catchError((error) => {
        ctx.patchState({ loading: false, fetching: false });
        return throwError(() => error);
      })
    );
  }

  @Action(LoadMoreTeamBorrowerDrillDown)
  loadMoreTeamBorrowerDrillDown(
    ctx: StateContext<SalesInventoryBorrowersStateModel>,
    { teamId, query }: LoadMoreTeamBorrowerDrillDown
  ): Observable<BorrowerDrillDownPage> {
    const key = teamBorrowerDrillDownKey(teamId, query);
    const state = ctx.getState();
    const cursor = state.page[key]?.nextCursor ?? null;

    if (!cursor || !state.page[key]?.hasMore || state.loadingMore[key]) {
      return of({
        rows: state.borrowers[key] ?? [],
        nextCursor: cursor,
        hasMore: state.page[key]?.hasMore ?? false,
      });
    }

    ctx.patchState({ loadingMore: { ...state.loadingMore, [key]: true } });

    return this._salesInventoryService.getTeamBorrowers(teamId, query, cursor).pipe(
      tap((page) => {
        const current = ctx.getState();
        const existing = current.borrowers[key] ?? [];
        // The api dedupes shared borrowers per request; skip any id already loaded so a
        // borrower re-assigned between page fetches can't appear twice (getRowId is borrowerId).
        const seen = new Set(existing.map((b) => b.borrowerId));
        ctx.patchState({
          borrowers: {
            ...current.borrowers,
            [key]: [...existing, ...page.rows.filter((b) => !seen.has(b.borrowerId))],
          },
          page: { ...current.page, [key]: { nextCursor: page.nextCursor, hasMore: page.hasMore } },
          lastSync: { ...current.lastSync, [key]: new Date() },
          loadingMore: { ...current.loadingMore, [key]: false },
        });
      }),
      catchError((error) => {
        const current = ctx.getState();
        ctx.patchState({ loadingMore: { ...current.loadingMore, [key]: false } });
        return throwError(() => error);
      })
    );
  }
}
